import { t } from "i18next";
import { Athlete, AthleteMeasurement, AthletePerformance } from "../../interfaces/athlete";
import moment from 'moment';
import ReactECharts from 'echarts-for-react';
import chartsIcon from "../../assets/images/charts.svg";
import { useKpiLabels } from "../../hooks/useKpiLabels";
import { useState } from "react";
import { Form } from "react-bootstrap";

interface AthletePerformanceChartProps {
    performance: AthletePerformance[];
}

export function AthletePerformanceChart(props: AthletePerformanceChartProps) {
    const { kpiLabels } = useKpiLabels();
    const [activeTab, setActiveTab] = useState('perf1');

    // Validar que hay datos
    if (!props.performance || props.performance.length === 0) {
        return (
            <div className="athlete-performance-chart-container">
                <div className="alert alert-info">
                    {t('No performance data available')}
                </div>
            </div>
        );
    }

    // Ordenar por fecha
    const sortedPerformance = [...props.performance].sort((a, b) =>
        moment(a.performance_date).valueOf() - moment(b.performance_date).valueOf()
    );

    // Extraer fechas y valores para cada KPI
    const dates = sortedPerformance.map(item => moment(item.performance_date).format('DD/MM/YYYY'));

    const perf1Values = sortedPerformance.map(item => parseFloat(item.perf1.toString()));
    const perf2Values = sortedPerformance.map(item => parseFloat(item.perf2.toString()));
    const perf3Values = sortedPerformance.map(item => parseFloat(item.perf3.toString()));
    const perf4Values = sortedPerformance.map(item => parseFloat(item.perf4.toString()));

    // Calcular estadísticas globales
    const calculateStats = (values: number[]) => {
        const max = Math.max(...values);
        const min = Math.min(...values);
        const avg = values.reduce((a, b) => a + b, 0) / values.length;
        return { max, min, avg };
    };

    const stats1 = calculateStats(perf1Values);
    const stats2 = calculateStats(perf2Values);
    const stats3 = calculateStats(perf3Values);
    const stats4 = calculateStats(perf4Values);

    // Función para crear opciones de gráfico de línea para cada KPI
    const createLineChartOptions = (
        kpiLabel: string,
        values: number[],
        stats: { max: number; min: number; avg: number }
    ) => {
        return {
            title: {
                text: kpiLabel,
                left: 'center',
                textStyle: {
                    fontSize: 18,
                    fontWeight: 'bold'
                }
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'cross'
                }
            },
            legend: {
                data: [t('Value'), t('Average'), t('Maximum'), t('Minimum')],
                top: '10%',
                left: 'center'
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '10%',
                top: '25%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: dates,
                axisLabel: {
                    rotate: 45
                }
            },
            yAxis: {
                type: 'value',
                name: t('Value'),
                axisLabel: {
                    formatter: '{value}'
                }
            },
            series: [
                {
                    name: t('Value'),
                    type: 'line',
                    data: values,
                    smooth: true,
                    lineStyle: {
                        width: 3,
                        color: '#5470c6'
                    },
                    itemStyle: {
                        color: '#5470c6'
                    },
                    areaStyle: {
                        color: {
                            type: 'linear',
                            x: 0,
                            y: 0,
                            x2: 0,
                            y2: 1,
                            colorStops: [
                                { offset: 0, color: 'rgba(84, 112, 198, 0.3)' },
                                { offset: 1, color: 'rgba(84, 112, 198, 0.05)' }
                            ]
                        }
                    }
                },
                {
                    name: t('Average'),
                    type: 'line',
                    data: new Array(dates.length).fill(stats.avg),
                    lineStyle: {
                        type: 'dashed',
                        width: 2,
                        color: '#fac858'
                    },
                    itemStyle: {
                        color: '#fac858'
                    },
                    symbol: 'none'
                },
                {
                    name: t('Maximum'),
                    type: 'line',
                    data: new Array(dates.length).fill(stats.max),
                    lineStyle: {
                        type: 'dashed',
                        width: 2,
                        color: '#91cc75'
                    },
                    itemStyle: {
                        color: '#91cc75'
                    },
                    symbol: 'none'
                },
                {
                    name: t('Minimum'),
                    type: 'line',
                    data: new Array(dates.length).fill(stats.min),
                    lineStyle: {
                        type: 'dashed',
                        width: 2,
                        color: '#ee6666'
                    },
                    itemStyle: {
                        color: '#ee6666'
                    },
                    symbol: 'none'
                }
            ]
        };
    };

    const optionsPerf1 = createLineChartOptions(kpiLabels.perf1 || 'KPI 1', perf1Values, stats1);
    const optionsPerf2 = createLineChartOptions(kpiLabels.perf2 || 'KPI 2', perf2Values, stats2);
    const optionsPerf3 = createLineChartOptions(kpiLabels.perf3 || 'KPI 3', perf3Values, stats3);
    const optionsPerf4 = createLineChartOptions(kpiLabels.perf4 || 'KPI 4', perf4Values, stats4);

    // Obtener las opciones del gráfico según el KPI seleccionado
    const getCurrentChartOptions = () => {
        switch (activeTab) {
            case 'perf1':
                return optionsPerf1;
            case 'perf2':
                return optionsPerf2;
            case 'perf3':
                return optionsPerf3;
            case 'perf4':
                return optionsPerf4;
            default:
                return optionsPerf1;
        }
    };

    return (
        <div className="athlete-performance-chart-container">
            <div className="kpi-selector mb-3">
                <Form.Group controlId="kpiSelect">
                    <Form.Label>{t('Select KPI')}</Form.Label>
                    <Form.Select
                        value={activeTab}
                        onChange={(e) => setActiveTab(e.target.value)}
                    >
                        <option value="perf1">{kpiLabels.perf1 || 'KPI 1'}</option>
                        <option value="perf2">{kpiLabels.perf2 || 'KPI 2'}</option>
                        <option value="perf3">{kpiLabels.perf3 || 'KPI 3'}</option>
                        <option value="perf4">{kpiLabels.perf4 || 'KPI 4'}</option>
                    </Form.Select>
                </Form.Group>
            </div>
            <div className="chart-content">
                <ReactECharts option={getCurrentChartOptions()} style={{ height: '400px' }} />
            </div>
        </div>
    );
}